import type { BinnableLeg } from "./binning-strategies";
import type { Rules } from "./helpers";

import { isBelowOrUnset, isResultFiltered } from "./helpers";

type FilterLimitOptions = "$empty" | "$full";

type TransformContext<T> = {
  has_unfiltered_results_map: Map<string, boolean>;
  allowed_count_map: Map<string, number>;
  excluded_results_set: Set<T>;
};

export type FilterStrategyBuilderActions<T, R, F> = {
  setKey: (key: keyof T) => FilterStrategyBuilderActions<T, R, F>;
  filterBy: (
    filter_options: Partial<{
      [k in keyof T]: Rules<T[k]>;
    }>
  ) => FilterStrategyBuilderActions<T, R, F>;
  setLimit: {
    filtered: (
      limit: number,
      conditions: Partial<{
        [k in FilterLimitOptions]: number;
      }>
    ) => FilterStrategyBuilderActions<T, R, F>;
  };
  transform: (
    transformer: (values: R[], context: TransformContext<R>) => F
  ) => FilterStrategyBuilderActions<T, R, F>;
  build: () => (values: T[], bin_key: string | number, bin_values: T[]) => F;
};

type FilterStrategyBuilderStrategies<T, R, F> = {
  key: keyof T;
  conditions?: Partial<{
    [k in keyof T]: Rules<T[k]>;
  }>;
  limits?: {
    filtered?: {
      limit: number;
      conditions?: Partial<{
        [k in FilterLimitOptions]: number;
      }>;
    };
  };
  transformer?: (values: R[], context: TransformContext<R>) => F;
};

export function filterStrategyBuilder<
  T = BinnableLeg, // Object to be binned
  BinValue = T, // Result from the binning strategy
  Transformed = BinValue[][] // Final transformed & formatted output
>(): FilterStrategyBuilderActions<T, BinValue, Transformed> {
  const strategies = {} as FilterStrategyBuilderStrategies<
    T,
    BinValue,
    Transformed
  >;
  const has_unfiltered_results_map = new Map<string, boolean>();
  const allowed_count_map = new Map<string, number>();
  const excluded_results_set = new Set<BinValue>();

  const builder = {
    filterBy(filter_options) {
      strategies.conditions = {
        ...(strategies.conditions || {}),
        ...filter_options
      } as any;
      return builder;
    },
    setLimit: {
      filtered(
        limit: number,
        conditions: Partial<{
          [k in FilterLimitOptions]: number;
        }>
      ) {
        strategies.limits = {
          ...(strategies.limits || {}),
          filtered: {
            limit,
            conditions
          }
        };
        return builder;
      }
    },
    transform(transformer) {
      strategies.transformer = transformer;
      return builder;
    },
    setKey(key: keyof T) {
      strategies.key = key;
      return builder;
    },
    build() {
      if (!strategies.key) {
        throw new Error(
          "FilterStrategyBuilder: No key defined for filter strategy"
        );
      }

      return (values: T[], bin_key: string | number, bin_values: T[]) => {
        const bin_key_string = String(bin_key);
        const results = values.reduce((acc, value) => {
          let is_filtered = false;
          let is_allowed = false;

          if (strategies.conditions) {
            is_filtered = isResultFiltered(value, strategies.conditions);
          }

          if (!is_filtered) {
            has_unfiltered_results_map.set(bin_key_string, true);
            acc.push(value[strategies.key] as BinValue);
            return acc;
          }

          const allowed_count = allowed_count_map.get(bin_key_string) || 0;

          if (strategies.limits?.filtered) {
            const { limit, conditions } = strategies.limits.filtered;
            const is_competing_with_unfiltered =
              has_unfiltered_results_map.get(bin_key_string);

            const is_reduce_condition_met: boolean =
              !!limit && isBelowOrUnset(allowed_count, limit);

            const is_empty_condition_met: boolean =
              !!conditions?.$empty &&
              !is_competing_with_unfiltered &&
              isBelowOrUnset(allowed_count, conditions.$empty) &&
              !!strategies.conditions &&
              !bin_values.some(
                result =>
                  !isResultFiltered(result, strategies.conditions as any)
              );

            is_allowed = is_empty_condition_met || is_reduce_condition_met;
          }

          if (is_allowed) {
            allowed_count_map.set(bin_key_string, allowed_count + 1);
            acc.push(value[strategies.key] as BinValue);
          } else {
            excluded_results_set.add(value[strategies.key] as BinValue);
          }

          return acc;
        }, [] as BinValue[]);

        if (strategies.transformer) {
          return strategies.transformer(results, {
            has_unfiltered_results_map,
            allowed_count_map,
            excluded_results_set
          });
        }

        return results;
      };
    }
  } as FilterStrategyBuilderActions<T, BinValue, Transformed>;

  return builder;
}
